import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAudioStore from '../store/audioStore';
import CardMetaLabel from './CardMetaLabel';

const SurahList = ({ surahs }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const { playFullSurah, currentSurah, status } = useAudioStore();
  
  if (surahs.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="text-5xl mb-4">📖</div>
        <p className="text-lg font-medium" style={{ color: 'var(--text-secondary)' }}>
          No surahs found
        </p>
      </div>
    );
  }

  const handlePlayClick = (e, surahNumber) => {
    e.preventDefault();
    e.stopPropagation();
    playFullSurah(surahNumber);
  };
  
  const isPlaying = (surahNumber) => {
    return currentSurah === surahNumber && status === 'playing';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {surahs.map((surah, idx) => {
        const playing = isPlaying(surah.number);
        const hovered = hoveredCard === surah.number;

        return (
          <div
            key={surah.number}
            className="group relative"
            onMouseEnter={() => setHoveredCard(surah.number)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <Link
              to={`/surah/${surah.number}`}
              className="block"
            >
              <div
                className="relative h-full p-8 rounded-2xl transition-all duration-300 cursor-pointer"
                style={{
                  background: playing ? 'rgba(212, 175, 55, 0.05)' : 'var(--bg-secondary)',
                  border: playing ? '2px solid var(--gold-primary)' : '1px solid var(--border-default)',
                  transform: hovered ? 'translateY(-8px)' : 'translateY(0)',
                  boxShadow: hovered
                    ? '0 12px 32px rgba(0, 0, 0, 0.2), 0 0 20px rgba(212, 175, 55, 0.15)'
                    : playing
                    ? '0 0 30px rgba(212, 175, 55, 0.2)'
                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                }}
              >
                {/* Revelation order position — top-right corner */}
                <span
                  className="absolute top-4 right-5 text-xs font-semibold tabular-nums"
                  style={{ color: 'var(--text-secondary)', opacity: 0.35 }}
                  title={`Revelation order: ${idx + 1}`}
                >
                  #{idx + 1}
                </span>

                {/* Surah Number */}
                <CardMetaLabel label="Surah" number={surah.number} />

                {/* Arabic Name - Hero */}
                <h2 
                  className="text-arabic text-4xl font-bold mb-2 text-right"
                  style={{ 
                    color: hovered ? 'var(--gold-hover)' : 'var(--text-arabic)',
                    transition: 'color 300ms ease-out'
                  }}
                >
                  {surah.name}
                </h2>

                {/* English Name */}
                <p 
                  className="text-xl font-semibold mb-1"
                  style={{ color: 'var(--gold-hover)' }}
                >
                  {surah.englishName}
                </p>

                {/* Translation */}
                <p 
                  className="text-sm mb-6"
                  style={{ 
                    color: 'var(--text-secondary)',
                    opacity: '0.8'
                  }}
                >
                  {surah.englishNameTranslation}
                </p>

                {/* Metadata Badges */}
                <div className="flex gap-2 flex-wrap mb-4">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: 'var(--button-default)',
                      color: 'var(--text-english)',
                      border: '1px solid var(--border-strong)',
                    }}
                  >
                    {surah.revelationType === 'Meccan' ? '🕋 Meccan' : '🕌 Medinan'}
                  </span>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: 'var(--button-default)',
                      color: 'var(--text-english)',
                      border: '1px solid var(--border-strong)',
                    }}
                  >
                    📚 {surah.numberOfAyahs} Verses
                  </span>
                </div>

                {/* Action Buttons - Appear on Hover */}
                {(hovered || playing) && (
                  <div 
                    className="flex gap-3 mt-4 animate-fade-in"
                    style={{
                      animation: 'fade-in 200ms ease-out'
                    }}
                  >
                    {/* Play Button */}
                    <button
                      onClick={(e) => handlePlayClick(e, surah.number)}
                      className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 active:scale-95"
                      style={{
                        background: playing 
                          ? 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))'
                          : 'var(--gold-muted)',
                        border: `2px solid ${playing ? 'var(--gold-primary)' : 'var(--border-gold)'}`,
                        color: playing ? 'white' : 'var(--gold-hover)',
                        fontWeight: '600',
                        minHeight: '44px', // Enhanced touch target
                      }}
                      onMouseEnter={(e) => {
                        if (!playing) {
                          e.currentTarget.style.background = 'rgba(212, 175, 55, 0.25)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!playing) {
                          e.currentTarget.style.background = 'var(--gold-muted)';
                        }
                      }}
                    >
                      {playing ? '⏸ Pause' : '▶ Play'}
                    </button>

                    {/* Read Button */}
                    <button
                      className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 active:scale-95"
                      style={{
                        background: 'var(--button-default)',
                        border: '2px solid var(--border-strong)',
                        color: 'var(--text-english)',
                        fontWeight: '600',
                        minHeight: '44px', // Enhanced touch target
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--button-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--button-default)';
                      }}
                    >
                      📖 Read
                    </button>
                  </div>
                )}
              </div>
            </Link>
          </div>
        );
      })}
    </div>
  );
};

export default SurahList;
